import React, { FC, FormEventHandler, useEffect, useState } from 'react'
import { Transfer } from '../../types'
import EditableDetailRow from './EditableDetailRow'
import useAxios from '../../hooks/useAxios'
import { isAxiosError } from 'axios'
import SummaryRowFormGroup from '../SummaryRowFormGroup'
import { logError } from '../../store/slices/app'
import { useDispatch } from 'react-redux'

interface Props {
  transfer: Transfer
  attribute: string // Backend API attribute
  label: string
  onUpdated?: (transfer: Transfer) => void
}

interface TransferUpdateResponse {
  transfer: Transfer
}

const EditableTransferDetailRow: FC<Props> = ({ attribute, label, transfer, onUpdated }) => {
  const instance = useAxios()

  const [error, setError] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [value, setValue] = useState(transfer[attribute] || '')
  const dispatch = useDispatch()

  useEffect(() => {
    setValue(transfer[attribute])
  }, [transfer, attribute])

  const handleSave: FormEventHandler = async (e) => {
    e.preventDefault()
    if (isLoading) return

    setIsLoading(true)
    setError('')

    try {
      const response = await instance.put<TransferUpdateResponse>(`ac/v1/transfers/${transfer.booking_id}`, {
        ...transfer,
        [attribute]: value,
      })

      onUpdated?.(response.data.transfer)
      setIsEditing(false)
    } catch (e) {
      dispatch(logError(e))
      let message = 'Sorry an unknown error occurred.'

      if (isAxiosError(e)) {
        console.log(e)
        if (e.response?.data?.message) {
          message = e.response.data.message
        }

        if (e.response?.data?.data?.params[attribute]) {
          message = e.response?.data?.data?.params[attribute]
        }

        if (Array.isArray(e.response?.data?.errors)) {
          message = e.response?.data?.errors[0]
        }
      }

      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const onCancel = () => {
    if (isLoading) return

    setIsEditing(false)
    setError('')
    setValue(transfer[attribute] || '')
  }

  return (
    <EditableDetailRow isEditing={isEditing} setIsEditing={setIsEditing} label={label} value={transfer[attribute]}>
      <form onSubmit={handleSave}>
        <SummaryRowFormGroup
          label={label}
          error={error}
          value={value}
          isLoading={isLoading}
          onChange={(e) => setValue(e.target.value)}
          onCancel={onCancel}
        />
      </form>
    </EditableDetailRow>
  )
}

export default EditableTransferDetailRow
